import type {
  TrainingModuleDetail,
  TrainingModulePayload,
  TrainingModuleSummary,
  TrainingReorderRow,
  TrainingTopicPayload,
} from "@xop/dashboard-shared";

import { ApiError, api, apiBlob, apiUploadForm } from "../../../lib/api.js";

/**
 * Client for Documentation ▸ Online Training. Every call goes to the
 * dashboard server's `/api/documentation/training-*` routes, which proxy to
 * portal-api's admin training endpoints with the staff documentation auth
 * (see apps/server/src/routes/documentationTraining.ts). Every write answers
 * with the WHOLE module detail, so the editor simply replaces its copy.
 */
const BASE = "/api/documentation";

/** React Query keys — the list, and one module (by the id in the URL). */
export const TRAINING_LIST_KEY = ["training", "modules"] as const;
export const trainingModuleKey = (id: string | number): readonly unknown[] => ["training", "module", String(id)];

type One = { data: TrainingModuleDetail };

export async function listTrainingModules(): Promise<TrainingModuleSummary[]> {
  const res = await api<{ data: TrainingModuleSummary[] }>(`${BASE}/training-modules`);
  return res?.data ?? [];
}

export async function getTrainingModule(id: number | string): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-modules/${encodeURIComponent(String(id))}`)).data;
}

export async function createTrainingModule(payload: TrainingModulePayload): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-modules`, { method: "POST", body: { training_module: payload } })).data;
}

export async function updateTrainingModule(id: number, payload: TrainingModulePayload): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-modules/${id}`, { method: "PATCH", body: { training_module: payload } })).data;
}

export async function deleteTrainingModule(id: number): Promise<void> {
  await api<void>(`${BASE}/training-modules/${id}`, { method: "DELETE" });
}

/** The WHOLE menu's order, one request, one transaction upstream. */
export async function reorderTrainingTopics(id: number, topics: TrainingReorderRow[]): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-modules/${id}/reorder`, { method: "PUT", body: { topics } })).data;
}

export async function createTrainingTopic(moduleId: number, payload: TrainingTopicPayload): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-modules/${moduleId}/topics`, { method: "POST", body: { topic: payload } })).data;
}

export async function updateTrainingTopic(topicId: number, payload: TrainingTopicPayload): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-topics/${topicId}`, { method: "PATCH", body: { topic: payload } })).data;
}

export async function deleteTrainingTopic(topicId: number): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-topics/${topicId}`, { method: "DELETE" })).data;
}

export async function uploadTrainingVideo(topicId: number, file: File): Promise<TrainingModuleDetail> {
  const form = new FormData();
  form.append("file", file, file.name);
  return (await apiUploadForm<One>(`${BASE}/training-topics/${topicId}/video`, form)).data;
}

export async function removeTrainingVideo(topicId: number): Promise<TrainingModuleDetail> {
  return (await api<One>(`${BASE}/training-topics/${topicId}/video`, { method: "DELETE" })).data;
}

/** An uploaded video's bytes, through the staff relay path the server put in
 *  `preview_url`. Fetched with the bearer (a `<video src>` cannot carry one —
 *  and the desktop shell has no cookie) and played from an object URL. */
export function fetchTrainingVideoBlob(relayPath: string): Promise<Blob> {
  return apiBlob(relayPath);
}

/** The sentence to show for a failed training call. A 503 `NOT_READY` means
 *  portal-api has not migrated the training tables yet — say so plainly. */
export function trainingErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const upstreamCode = (err.details as { upstream_body?: { code?: unknown } } | undefined)?.upstream_body?.code;
    if (upstreamCode === "NOT_READY") return "Online Training isn't switched on in the Support Center yet.";
    if (upstreamCode === "TRAINING_EDIT_FORBIDDEN") return "Only a full documentation editor can change training.";
  }
  return err instanceof Error ? err.message : "Something went wrong";
}
